import { useEffect, useState } from "react"
import { fetchCardTotal,fetchFilters } from "../Components/utils/services/headerBarServices"

export const useFetchDashboard = () => {
  const [exceptionsAndStates, setExceptionsAndStates] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Await all promises first, THEN transform
        const [
          inventoryExceptionsRes,
          inboundExceptionsRes,
          inboundStatesRes,
          outboundExceptionsRes,
          outboundStatesRes,
          purchaseOrderExceptionsRes,
          purchaseOrderStatesRes,
          salesOrderExceptionsRes,
          salesOrderStatesRes,
        ] = await Promise.all([
          fetchFilters('Inventory', 'Shipment', 'Exceptions'),
          fetchFilters('Supply', 'Shipment', 'Exceptions'),
          fetchCardTotal('Supply', 'Shipment', 'States'),
          fetchFilters('Demand', 'Shipment', 'Exceptions'),
          fetchCardTotal('Demand', 'Shipment', 'States'),
          fetchFilters('Supply', 'Order', 'Exceptions'),
          fetchCardTotal('Supply', 'Order', 'States'),
          fetchFilters('Demand', 'Order', 'Exceptions'),
          fetchCardTotal('Demand', 'Order', 'States'),
        ])

        // Now transform the resolved data
        const [
          inventoryExceptions,
          inboundShipmentExceptions,
          inboundShipmentStates,
          outboundShipmentExceptions,
          outboundShipmentStates,
          purchaseOrderExceptions,
          purchaseOrderStates,
          salesOrderExceptions,
          salesOrderStates,
        ] = await Promise.all([
          transformShipmentExceptions(inventoryExceptionsRes, 'inventory'),
          transformShipmentExceptions(inboundExceptionsRes, 'inbound'),
          transformShipmentStates(inboundStatesRes, 'inbound'),
          transformShipmentExceptions(outboundExceptionsRes, 'outbound'),
          transformShipmentStates(outboundStatesRes, 'outbound'),
          transformShipmentExceptions(purchaseOrderExceptionsRes, 'purchaseOrder'),
          transformShipmentStates(purchaseOrderStatesRes, 'purchaseOrder'),
          transformShipmentExceptions(salesOrderExceptionsRes, 'salesOrder'),
          transformShipmentStates(salesOrderStatesRes, 'salesOrder'),
        ])

        // Set state once with all data (single update, no multiple loops)
        setExceptionsAndStates({
          inventoryExceptions,
          inboundShipmentExceptions,
          inboundShipmentStates,
          outboundShipmentExceptions,
          outboundShipmentStates,
          purchaseOrderExceptions,
          purchaseOrderStates,
          salesOrderExceptions,
          salesOrderStates,
        })
      } catch (error) {
       
        setExceptionsAndStates({
          inventoryExceptions: null,
          inboundShipmentExceptions: null,
          inboundShipmentStates: null,
          outboundShipmentExceptions: null,
          outboundShipmentStates: null,
          purchaseOrderExceptions: null,
          purchaseOrderStates: null,
          salesOrderExceptions: null,
          salesOrderStates: null,
        })
      }
    }
    fetchData()
  }, [])

  return exceptionsAndStates
}

export const transformShipmentStates = async (response, id) => {
  // Response is already resolved (no need for await)
  const data = response.data

  return {
    id,
    type: 'states',
    data: data?.map(item => {
      switch (item?.detailName) {
        case 'Confirmed':
          return { label: item.detailName, value: Number(item.count), active: item.active, color: '#E5813E' }
        case 'Confirmed with changes':
        case 'In Transit':
        case 'Processing':
          return { label: item.detailName, value: Number(item.count), active: item.active, color: '#F79900' }
        case 'Planned Shipment':
        case 'Pending':
          return { label: item.detailName, value: Number(item.count), active: item.active, color: '#D7B85B' }
        case 'Shipped':
          return { label: item.detailName, value: Number(item.count), active: item.active, color: '#B59E74' }
        case 'Delivered':
        case 'Completed':
          return { label: item.detailName, value: Number(item.count), active: item.active, color: '#6ACE7F' }
        case 'Closed':
          return { label: item.detailName, value: Number(item.count), active: item.active, color: '#F40000' }
        case 'In-Transit Cancelled':
        case 'Cancelled':
          return { label: item.detailName, value: Number(item.count), active: item.active, color: '#FF560E' }
        case 'No State':
        case 'Rejected':
          return { label: item.detailName, value: Number(item.count), active: item.active, color: '#6AC9CE' }
        default:
          return { label: item.detailName, value: Number(item.count), active: item.active, color: '#000000' }
      }
    }) || [],
    totalCount: data?.reduce((total, item) => total + Number(item.count), 0) || 0,
    totalShipments: Number(response?.metadata?.totalCount) || 0,
  }
}

export const transformShipmentExceptions = async (response, id) => {
  const data = response

  return {
    id,
    type: 'exceptions',
    data: data
      ?.map(item => {
        const label = item.detailName
        switch (label) {
          case 'Untracked':
          case 'Stockout':
          case 'Cancelled by Customer':
            return { label: item.detailName, value: Number(item.count), color: '#F40000' }
          case 'Late':
          case 'Late Arrival':
          case 'Pre Detention Breach':
          case 'Past Due':
          case 'Low Stock':
            return { label: item.detailName, value: Number(item.count), color: '#FF560E' }
          case 'PTA Unknown':
          case 'Batch Expired':
            return { label: item.detailName, value: Number(item.count), color: '#E5813E' }
          case 'Early':
          case 'Over Stock':
            return { label: item.detailName, value: Number(item.count), color: '#BC6BD9' }
          case 'Delayed':
          case 'Unconfirmed':
          case 'Batch Expiring':
            return { label: item.detailName, value: Number(item.count), color: '#F79900' }
          case 'Detention Breach':
          case 'Damaged Goods':
          case 'Payment Failed':
            return { label: item.detailName, value: Number(item.count), color: '#F40000' }
          case 'Dwell Time Breach':
          case 'Temperature Violation':
            return { label: item.detailName, value: Number(item.count), color: '#F79900' }
          case 'Demurrage Breach':
            return { label: item.detailName, value: Number(item.count), color: '#6AC9CE' }
           case 'On Time':
           case 'On Track':
            return { label: item.detailName, value: Number(item.count), color: '#268000' }
          default:
            return { label: item.detailName, value: Number(item.count), color: '#000000' }
        }
      }) || [],
    totalCount: data?.reduce((total, item) => total + Number(item.count), 0) || 0,
   
  }
}